import * as React from 'react';
import { visuallyHidden } from '../../../mixins.css';
import {
  addedCount,
  empty as emptyCls,
  gutter,
  line,
  list,
  removedCount,
  root as rootCls,
  summary,
} from './DraftDiff.css';
import { diffLines } from './diffLines';

export type DraftDiffSize = 'sm' | 'md';

export interface DraftDiffProps extends React.HTMLAttributes<HTMLDivElement> {
  /** The older text. Empty = everything in `after` is added. */
  before: string;
  /** The newer text. */
  after: string;
  size?: DraftDiffSize;
  /** React 19 ref-as-prop. */
  ref?: React.Ref<HTMLDivElement>;
}

const GLYPH = { same: ' ', added: '+', removed: '−' } as const;
const SR_LABEL = { same: '', added: 'Added: ', removed: 'Removed: ' } as const;

/**
 * DraftDiff — the line diff between two revisions of a text, for the Agent
 * Builder's draft history (ADR-0080, AB-2-2). Added lines render as `<ins>`,
 * removed as `<del>`, each with a `+`/`−` gutter and a screen-reader prefix, so
 * the change never depends on colour. A summary line counts both.
 */
export function DraftDiff({
  before,
  after,
  size = 'md',
  ref,
  className,
  'aria-label': ariaLabel = 'Changes',
  ...rest
}: DraftDiffProps) {
  const lines = diffLines(before, after);
  const added = lines.filter((l) => l.kind === 'added').length;
  const removed = lines.filter((l) => l.kind === 'removed').length;
  const cls = [rootCls({ size }), className].filter(Boolean).join(' ');
  return (
    <div ref={ref} role="group" aria-label={ariaLabel} className={cls} {...rest}>
      {added + removed === 0 ? (
        <p className={emptyCls}>No changes.</p>
      ) : (
        <>
          <p className={summary}>
            <span className={addedCount} aria-hidden="true">
              +{added}
            </span>
            <span className={removedCount} aria-hidden="true">
              −{removed}
            </span>
            <span className={visuallyHidden}>
              {added} added, {removed} removed
            </span>
          </p>
          <ol className={list}>
            {lines.map((l, i) => {
              const Tag = l.kind === 'added' ? 'ins' : l.kind === 'removed' ? 'del' : 'span';
              return (
                // Index keys are right here: the list is recomputed whole, never reordered.
                <li key={i}>
                  <Tag className={line({ kind: l.kind })}>
                    <span className={gutter} aria-hidden="true">
                      {GLYPH[l.kind]}
                    </span>
                    {SR_LABEL[l.kind] && <span className={visuallyHidden}>{SR_LABEL[l.kind]}</span>}
                    <span>{l.text || ' '}</span>
                  </Tag>
                </li>
              );
            })}
          </ol>
        </>
      )}
    </div>
  );
}
DraftDiff.displayName = 'DraftDiff';
